import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import {
    getStaffQuarterCurrentRegistration,
    getUserStaffQuarterRequests,
    getStaffQuarterDetails,
    getStaffQuarterPriorityList,
    getEmployeeDetailsForStaffQuarter,
    getMyAllotments,
    getAllWillingness,
    submitStaffQuarterUnwillingness
} from "../../../../core/data/redux/staffQuarterSlice";
import { formatFlatNoWithBlock } from "../staffQuarterUtils";
import { Spin, Modal, message } from "antd";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

const Unwillingness: React.FC = () => {
    const routes = all_routes as any;
    const dispatch = useDispatch<any>();

    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
    const employeeDetails = staffQuarterState?.employeeDetails || {};
    const currentRegistration = staffQuarterState?.currentRegistration?.data || {};
    const userRequests = staffQuarterState?.requests?.data || staffQuarterState?.requests || [];
    const currentRequest = staffQuarterState?.currentRequest?.data || staffQuarterState?.currentRequest || {};

    const [loading, setLoading] = useState(false);
    const [priorityInfo, setPriorityInfo] = useState<any>(null);
    const [_loadingPriority, setLoadingPriority] = useState(false);
    const [selectedLocationTab, setSelectedLocationTab] = useState<string>("ALL");

    // Modal state for Submitting Unwillingness
    const [unwillingnessModalOpen, setUnwillingnessModalOpen] = useState(false);
    const [selectedOfferForUnwillingness, setSelectedOfferForUnwillingness] = useState<any>(null);
    const [unwillingnessRemarks, setUnwillingnessRemarks] = useState("");
    const [isSubmittingUnwillingness, setIsSubmittingUnwillingness] = useState(false);

    // Fetch initial employee and registration data
    useEffect(() => {
        if (!employeeDetails?.employeeNumber) {
            dispatch(getEmployeeDetailsForStaffQuarter({}));
        }
        dispatch(getStaffQuarterCurrentRegistration({}));
        dispatch(getMyAllotments({}));
    }, [dispatch]);

    useEffect(() => {
        if (employeeDetails?.employeeNumber) {
            dispatch(getUserStaffQuarterRequests({ employeeId: employeeDetails.employeeNumber }));
            dispatch(getAllWillingness({ employeeId: employeeDetails.employeeNumber }));
        }
    }, [dispatch, employeeDetails?.employeeNumber]);

    // Active registration request
    const activeRequest = useMemo(() => {
        const list = Array.isArray(userRequests)
            ? userRequests
            : (Array.isArray(userRequests?.requests) ? userRequests.requests : []);

        const matched = list.find((req: any) =>
            String(req.referenceNumber) === String(currentRegistration?.referenceNumber) ||
            String(req.registrationId) === String(currentRegistration?.registrationId) ||
            String(req.id) === String(currentRegistration?.registrationId) ||
            String(req.id) === String(currentRegistration?.id)
        ) || list[0] || currentRegistration || currentRequest;

        return matched || {};
    }, [userRequests, currentRegistration, currentRequest]);

    // Load full details for the active registration request
    useEffect(() => {
        const regId = activeRequest?.id || activeRequest?.registrationId || currentRegistration?.id || currentRegistration?.registrationId;
        if (regId) {
            setLoading(true);
            dispatch(getStaffQuarterDetails({ id: regId })).finally(() => setLoading(false));
        }
    }, [dispatch, activeRequest?.id, activeRequest?.registrationId, currentRegistration?.id, currentRegistration?.registrationId]);

    const data = currentRequest?.id ? currentRequest : (activeRequest || {});

    // Fetch Priority Ranking
    useEffect(() => {
        if (data) {
            const qCode = data.quarterTypeEligibility?.quarterTypeCode ||
                data.quarterTypeEligibility?.quarter_type_code ||
                data.quarterTypeEligibility?.code ||
                data.quarterTypeEligibility?.id ||
                employeeDetails?.quarterTypeEligibility?.id;
            const locId = data.locationPreferences?.[0]?.id || data.locationPreference1Id || data.locationPreference1_id;

            if (qCode && locId) {
                setLoadingPriority(true);
                dispatch(getStaffQuarterPriorityList({
                    page: 1,
                    limit: 100,
                    quarter_type_code: isNaN(Number(qCode)) ? qCode : Number(qCode),
                    locationId: isNaN(Number(locId)) ? locId : Number(locId),
                    ...(employeeDetails.employeeNumber && { empId: employeeDetails.employeeNumber }),
                    ...(data.pool?.id && { poolTypeId: data.pool.id })
                })).then((res: any) => {
                    setLoadingPriority(false);
                    const list = res.payload?.data?.list || res.payload?.data?.data || (Array.isArray(res.payload?.data) ? res.payload.data : []);
                    if (Array.isArray(list)) {
                        const match = list.find((p: any) =>
                            String(p.employeeNumber || p.empId || p.employeeId) === String(employeeDetails.employeeNumber || data.employeeNumber || data.empId) ||
                            String(p.registrationReferenceNumber || p.referenceNumber) === String(data.referenceNumber)
                        );
                        if (match) setPriorityInfo(match);
                    }
                }).catch(() => {
                    setLoadingPriority(false);
                });
            }
        }
    }, [data, employeeDetails.employeeNumber, dispatch]);

    // Categorize willingness and unwillingness records into distinct lists
    const { approvedWillingnessOffers, approvedUnwillingnessRequests, pendingUnwillingnessRequests, allDisplayRequests } = useMemo(() => {
        const sources = [
            data?.unwillingnessDetails,
            data?.unwillingness,
            data?.unwillingnessRequests,
            data?.unwillingnessList,
            data?.willingnessDetails,
            data?.willingness,
            data?.willingnessRequests,
            data?.willingnessList,
            data?.allotmentDetails,
            data?.allotments,
            currentRegistration?.unwillingnessDetails,
            currentRegistration?.unwillingness,
            currentRegistration?.unwillingnessRequests,
            currentRegistration?.unwillingnessList,
            currentRegistration?.willingnessDetails,
            currentRegistration?.willingness,
            currentRegistration?.willingnessRequests,
            currentRegistration?.willingnessList,
            currentRegistration?.allotmentDetails,
            currentRegistration?.allotments,
            currentRequest?.unwillingnessDetails,
            currentRequest?.unwillingness,
            currentRequest?.unwillingnessRequests,
            currentRequest?.unwillingnessList,
            currentRequest?.willingnessDetails,
            currentRequest?.willingness,
            currentRequest?.willingnessRequests,
            currentRequest?.willingnessList,
            currentRequest?.allotmentDetails,
            currentRequest?.allotments,
            staffQuarterState?.myAllotments?.data,
            staffQuarterState?.myAllotments,
            staffQuarterState?.allWillingness?.data,
            staffQuarterState?.allWillingness,
            ...(Array.isArray(userRequests) ? userRequests : (userRequests?.requests || []))
        ];

        const allRawItems: any[] = [];
        sources.forEach((src) => {
            if (!src) return;
            if (Array.isArray(src)) {
                allRawItems.push(...src);
            } else if (typeof src === "object") {
                allRawItems.push(src);
            }
        });

        const seenKeys = new Set();
        const willingnessList: any[] = [];
        const unwillingnessApprovedList: any[] = [];
        const unwillingnessPendingList: any[] = [];
        const combinedList: any[] = [];

        allRawItems.forEach((item: any) => {
            if (!item || typeof item !== "object") return;

            const itemId = item.id || item.approvalId || item.willingnessId || item.unwillingnessId || item.referenceNumber || item.willingnessReferenceNumber || item.registrationReferenceNumber || item.flatNo || item.flat_no;
            const uniqueKey = `${itemId}_${item.status || item.willingnessStatus || item.unwillingnessStatus}_${item.location || item.locationName || item.flatNo || ''}`;

            if (seenKeys.has(uniqueKey)) return;

            const st = String(item.status || "").toUpperCase();
            const wSt = String(item.willingnessStatus || "").toUpperCase();
            const unwSt = String(item.unwillingnessStatus || item.unwillingnessDetails?.status || "").toUpperCase();

            // Check if item represents explicitly approved unwillingness
            const isUnwillingnessApproved =
                unwSt === "APPROVED" ||
                st === "UNWILLING" ||
                st === "UNWILLINGNESS_APPROVED" ||
                st === "APPROVED_UNWILLINGNESS" ||
                item.isUnwillingnessApproved === true ||
                (item.unwillingnessDetails && String(item.unwillingnessDetails.status).toUpperCase() === "APPROVED");

            // Check if item represents pending unwillingness
            const isUnwillingnessPending =
                unwSt === "PENDING" ||
                st === "UNWILLINGNESS_PENDING" ||
                st === "UNWILLING_PENDING" ||
                (item.unwillingnessDetails && String(item.unwillingnessDetails.status).toUpperCase() === "PENDING");

            // Check if item represents approved willingness offer (allotment option ready for unwillingness response)
            const isWillingnessApproved =
                !isUnwillingnessApproved &&
                !isUnwillingnessPending &&
                (wSt === "APPROVED" || st === "APPROVED" || st === "CONFIRMED" || st === "ACCEPTED" || st === "ALLOTTED" || item.isWillingnessApproved === true || item.isWilling === true);

            const isRejected = st === "REJECTED" || st === "CANCELLED" || st === "DRAFT" || st === "DELETED";

            if (isRejected) return;

            if (isUnwillingnessApproved) {
                seenKeys.add(uniqueKey);
                const enriched = { ...item, _category: "UNWILLINGNESS_APPROVED" };
                unwillingnessApprovedList.push(enriched);
                combinedList.push(enriched);
            } else if (isUnwillingnessPending) {
                seenKeys.add(uniqueKey);
                const enriched = { ...item, _category: "UNWILLINGNESS_PENDING" };
                unwillingnessPendingList.push(enriched);
                combinedList.push(enriched);
            } else if (isWillingnessApproved) {
                seenKeys.add(uniqueKey);
                const enriched = { ...item, _category: "WILLINGNESS_APPROVED" };
                willingnessList.push(enriched);
                combinedList.push(enriched);
            }
        });

        return {
            approvedWillingnessOffers: willingnessList,
            approvedUnwillingnessRequests: unwillingnessApprovedList,
            pendingUnwillingnessRequests: unwillingnessPendingList,
            allDisplayRequests: combinedList
        };
    }, [data, currentRegistration, currentRequest, staffQuarterState?.myAllotments, staffQuarterState?.allWillingness, userRequests]);

    // Group willingness and unwillingness requests location-wise
    const locationGroupedRequests = useMemo(() => {
        const groups: { [locName: string]: any[] } = {};
        allDisplayRequests.forEach((reqObj: any) => {
            const loc = (
                reqObj.location ||
                reqObj.locationName ||
                reqObj.location_name ||
                reqObj.locationPreference ||
                reqObj.flatLocation ||
                reqObj.quarterLocation ||
                reqObj.flats?.[0]?.location ||
                reqObj.flats?.[0]?.locationName ||
                reqObj.flats?.[0]?.location_name ||
                data?.locationPreferences?.[0]?.name ||
                data?.locationPreference1 ||
                "Other Location"
            ).trim();

            if (!groups[loc]) {
                groups[loc] = [];
            }
            groups[loc].push(reqObj);
        });
        return groups;
    }, [allDisplayRequests, data]);

    const locationNames = useMemo(() => {
        return Object.keys(locationGroupedRequests);
    }, [locationGroupedRequests]);

    const displayedLocationNames = useMemo(() => {
        if (selectedLocationTab === "ALL") return locationNames;
        return locationNames.filter((loc) => loc === selectedLocationTab);
    }, [selectedLocationTab, locationNames]);

    const handleOpenUnwillingnessModal = (offer: any) => {
        setSelectedOfferForUnwillingness(offer);
        setUnwillingnessRemarks("");
        setUnwillingnessModalOpen(true);
    };

    const handleSubmitUnwillingness = async () => {
        if (!selectedOfferForUnwillingness) return;
        if (!unwillingnessRemarks.trim()) {
            message.error("Please enter remarks for declaring unwillingness.");
            return;
        }

        const regId = Number(
            data?.id ||
            data?.registrationId ||
            selectedOfferForUnwillingness?.registrationId ||
            activeRequest?.id ||
            currentRegistration?.id ||
            0
        );

        const locId = Number(
            selectedOfferForUnwillingness?.locationId ||
            selectedOfferForUnwillingness?.location_id ||
            selectedOfferForUnwillingness?.locationPreferenceId ||
            selectedOfferForUnwillingness?.flats?.[0]?.locationId ||
            selectedOfferForUnwillingness?.flats?.[0]?.location_id ||
            data?.locationPreferences?.find((p: any) => p.name === selectedOfferForUnwillingness?.location || p.locationName === selectedOfferForUnwillingness?.location)?.id ||
            data?.locationPreference1Id ||
            1
        );

        const payload = {
            registrationId: regId,
            locationId: locId,
            remarks: unwillingnessRemarks.trim()
        };

        setIsSubmittingUnwillingness(true);
        try {
            const res = await dispatch(submitStaffQuarterUnwillingness(payload)).unwrap();
            message.success(res?.message || "Unwillingness request submitted successfully!");
            setUnwillingnessModalOpen(false);
            setSelectedOfferForUnwillingness(null);
            setUnwillingnessRemarks("");

            // Refresh registration & willingness data
            dispatch(getStaffQuarterCurrentRegistration({}));
            dispatch(getMyAllotments({}));
            if (employeeDetails?.employeeNumber) {
                dispatch(getUserStaffQuarterRequests({ employeeId: employeeDetails.employeeNumber }));
                dispatch(getAllWillingness({ employeeId: employeeDetails.employeeNumber }));
            }
            if (regId) {
                dispatch(getStaffQuarterDetails({ id: regId }));
            }
        } catch (error: any) {
            message.error(typeof error === "string" ? error : (error?.message || "Failed to submit unwillingness request"));
        } finally {
            setIsSubmittingUnwillingness(false);
        }
    };

    const formatDate = (date: any, includeTime: boolean = false) => {
        if (!date || date === "-" || date === "null" || date === "undefined") return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        if (!includeTime) return `${day} ${month}, ${year}`;
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day} ${month}, ${year} ${hours}:${minutes}`;
    };

    const isRegistered = !!(data && Object.keys(data).length > 0 && (data.id || data.referenceNumber || data.registrationId));

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                        <div>
                            <h3 className="page-title text-dark fw-bold mb-1">Staff Quarter Unwillingness</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1 mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.employeeDashboard || "#"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarter.list}>Staff Quarters</Link>
                                </li>
                                <li className="breadcrumb-item active">Unwillingness</li>
                            </ul>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {data?.id && (
                                <Link
                                    to={routes.staffQuarter.details.replace(":id", encodeRouteParam(data.id))}
                                    className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                >
                                    <i className="ti ti-eye me-1"></i> Full Application Details
                                </Link>
                            )}
                            <Link to={routes.staffQuarter.newRegistration} className="btn btn-danger btn-sm rounded-pill px-3">
                                <i className="ti ti-plus me-1"></i> New Registration
                            </Link>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="card border-0 shadow-sm p-5 text-center my-4" style={{ borderRadius: "12px" }}>
                        <Spin size="large" tip="Loading Unwillingness & Priority details..." />
                    </div>
                ) : !isRegistered ? (
                    <div className="card border-0 shadow-sm my-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body text-center py-5">
                            <div
                                className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light text-muted mb-3"
                                style={{ width: "64px", height: "64px" }}
                            >
                                <i className="ti ti-home-question fs-1"></i>
                            </div>
                            <h5 className="fw-bold text-dark mb-1">No Active Staff Quarter Registration Found</h5>
                            <p className="text-muted small mb-4" style={{ maxWidth: "460px", margin: "0 auto" }}>
                                You have not submitted a staff quarter registration yet. Please register to enter the priority list and view status.
                            </p>
                            <Link to={routes.staffQuarter.newRegistration} className="btn btn-danger px-4 rounded-pill">
                                Apply for Staff Quarter
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Top Metrics Cards */}
                        <div className="row g-3 mb-4">
                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", background: "#fff" }}>
                                    <div className="card-body p-3 d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                            style={{ width: "48px", height: "48px", backgroundColor: "rgba(13, 110, 253, 0.1)", color: "#0d6efd" }}
                                        >
                                            <i className="ti ti-list-numbers fs-3"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Priority Position</div>
                                            <div className="h4 fw-bold mb-0 text-dark">
                                                {priorityInfo?.priority || (priorityInfo?.priorityNo ? `#${priorityInfo.priorityNo}` : null) || data.priority || data.priorityNo || "Seniority"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", background: "#fff" }}>
                                    <div className="card-body p-3 d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                backgroundColor: approvedWillingnessOffers.length > 0 ? "rgba(25, 135, 84, 0.1)" : "rgba(108, 117, 125, 0.1)",
                                                color: approvedWillingnessOffers.length > 0 ? "#198754" : "#6c757d"
                                            }}
                                        >
                                            <i className="ti ti-circle-check fs-3"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Approved Willingness Offers</div>
                                            <div className="h4 fw-bold mb-0 text-success">{approvedWillingnessOffers.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", background: "#fff" }}>
                                    <div className="card-body p-3 d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                            style={{
                                                width: "48px",
                                                height: "48px",
                                                backgroundColor: approvedUnwillingnessRequests.length > 0 || data?.status === "UNWILLING" ? "rgba(220, 53, 69, 0.1)" : "rgba(111, 66, 193, 0.1)",
                                                color: approvedUnwillingnessRequests.length > 0 || data?.status === "UNWILLING" ? "#dc3545" : "#6f42c1"
                                            }}
                                        >
                                            <i className="ti ti-ban fs-3"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Unwillingness Status</div>
                                            <div className={`h4 fw-bold mb-0 ${approvedUnwillingnessRequests.length > 0 || data?.status === "UNWILLING" ? "text-danger" : (pendingUnwillingnessRequests.length > 0 ? "text-warning" : (approvedWillingnessOffers.length > 0 ? "text-success" : "text-secondary"))}`}>
                                                {approvedUnwillingnessRequests.length > 0 || data?.status === "UNWILLING"
                                                    ? "APPROVED"
                                                    : (pendingUnwillingnessRequests.length > 0
                                                        ? "PENDING"
                                                        : (approvedWillingnessOffers.length > 0 ? "WILLINGNESS OFFERED" : "NONE"))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-3 col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px", background: "#fff" }}>
                                    <div className="card-body p-3 d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                            style={{ width: "48px", height: "48px", backgroundColor: "rgba(13, 202, 240, 0.1)", color: "#0dcaf0" }}
                                        >
                                            <i className="ti ti-map-pin fs-3"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Location Preferences</div>
                                            <div className="h4 fw-bold mb-0 text-info">
                                                {data.locationPreferences?.length || (data.locationPreference1 ? [data.locationPreference1, data.locationPreference2, data.locationPreference3].filter(Boolean).length : 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Action Banner (Shown ONLY if Unwillingness has been explicitly approved or recorded) */}
                        {(approvedUnwillingnessRequests.length > 0 || data.status === "WITHDRAWN" || data.status === "UNWILLING") && (
                            <div
                                className="card mb-4 border-0 shadow-sm"
                                style={{
                                    background: "linear-gradient(135deg, #fff5f5 0%, #fff 100%)",
                                    borderRadius: "12px",
                                    borderLeft: "4px solid #dc3545"
                                }}
                            >
                                <div className="card-body p-3 p-md-4">
                                    <div className="d-flex align-items-start gap-3">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: "40px", height: "40px", background: "#fee2e2", color: "#dc3545" }}
                                        >
                                            <i className="ti ti-circle-x fs-2"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                                                <h5 className="fw-bold text-dark mb-0 fs-16">
                                                    Unwillingness / Declination Recorded
                                                </h5>
                                                <span className="badge bg-danger text-white px-3 py-1 fw-bold fs-12 rounded-pill">
                                                    DECLINED / UNWILLING
                                                </span>
                                            </div>
                                            <p className="text-secondary small mb-0" style={{ lineHeight: "1.6" }}>
                                                You have declared unwillingness / refusal for a quarter allotment opportunity offered under your eligible priority turn. As per DMRC allotment policy, debarment or cooldown restrictions apply on consequent priority cycles.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Details from Priority & Seniority */}
                        <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: "12px" }}>
                            <div className="card-header bg-white border-bottom py-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle text-primary"
                                        style={{ width: "32px", height: "32px", background: "rgba(13, 110, 253, 0.08)" }}
                                    >
                                        <i className="ti ti-id-badge-2" style={{ fontSize: "18px" }}></i>
                                    </div>
                                    <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                                        Details from Priority & Seniority
                                    </h5>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-3 col-sm-6">
                                        <div className="text-muted small fw-medium">Priority Rank / Position</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {priorityInfo?.priority || (priorityInfo?.priorityNo ? `#${priorityInfo.priorityNo}` : null) || data.priority || data.priorityNo || "Seniority Roster"}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6">
                                        <div className="text-muted small fw-medium">Registration Application Ref</div>
                                        <div className="fw-bold text-primary mt-1" style={{ fontSize: "14px" }}>
                                            {data.referenceNumber || data.registrationReferenceNumber || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6">
                                        <div className="text-muted small fw-medium">Seniority Date (DOJ / Registration)</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {formatDate(data.dateOfJoining || priorityInfo?.dateOfJoining || data.regDate || data.createdAt)}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6">
                                        <div className="text-muted small fw-medium">Priority List Status</div>
                                        <div className="mt-1">
                                            {approvedUnwillingnessRequests.length > 0 || data.status === "WITHDRAWN" || data.status === "UNWILLING" ? (
                                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">UNWILLING</span>
                                            ) : approvedWillingnessOffers.length > 0 ? (
                                                <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">WILLINGNESS OFFERED</span>
                                            ) : (
                                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">{priorityInfo?.status || data.status || "ACTIVE"}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3 col-sm-6 border-top pt-3">
                                        <div className="text-muted small fw-medium">Eligible Quarter Type</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {data.quarterTypeEligibility?.type
                                                ? data.quarterTypeEligibility.type.replace(/\s*accommodation\s*/gi, "")
                                                : (data.quarterTypeEligibility?.quarterType || priorityInfo?.quarterType || "-")}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 border-top pt-3">
                                        <div className="text-muted small fw-medium">Pool Category</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {data.pool?.name || priorityInfo?.pool || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 border-top pt-3">
                                        <div className="text-muted small fw-medium">Employee Category</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {data.category || priorityInfo?.category || "-"}
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 border-top pt-3">
                                        <div className="text-muted small fw-medium">Pay Scale / Basic Pay</div>
                                        <div className="fw-bold text-dark mt-1">
                                            {data.payScale ? `${data.payScale} (₹${data.basicPay || "-"})` : (data.basicPay ? `₹${data.basicPay}` : "-")}
                                        </div>
                                    </div>

                                    {/* Location Preferences */}
                                    <div className="col-12 border-top pt-3">
                                        <div className="text-muted small fw-medium mb-2">Location Preferences in Priority</div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {(() => {
                                                const prefs = [...(data.locationPreferences || [])].sort((a: any, b: any) => (a.preference || 0) - (b.preference || 0));
                                                if (prefs.length > 0) {
                                                    return prefs.map((p: any, idx: number) => (
                                                        <div key={idx} className="p-2 px-3 bg-light rounded border small d-flex align-items-center gap-2">
                                                            <span className="badge bg-primary rounded-circle" style={{ width: '20px', height: '20px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {p.preference || idx + 1}
                                                            </span>
                                                            <span className="fw-bold text-dark">{p.name || p.locationName || `Location #${idx + 1}`}</span>
                                                        </div>
                                                    ));
                                                }
                                                const fallbackPrefs = [data.locationPreference1, data.locationPreference2, data.locationPreference3].filter(Boolean);
                                                if (fallbackPrefs.length > 0) {
                                                    return fallbackPrefs.map((name: string, idx: number) => (
                                                        <div key={idx} className="p-2 px-3 bg-light rounded border small d-flex align-items-center gap-2">
                                                            <span className="badge bg-primary rounded-circle" style={{ width: '20px', height: '20px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {idx + 1}
                                                            </span>
                                                            <span className="fw-bold text-dark">{name}</span>
                                                        </div>
                                                    ));
                                                }
                                                return <span className="text-muted small">No specific location preferences recorded.</span>;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Willingness Offers & Unwillingness Requests Section (Location-Wise) */}
                        <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: "12px" }}>
                            <div className="card-header bg-white border-bottom py-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle text-primary"
                                        style={{ width: "32px", height: "32px", background: "rgba(13, 110, 253, 0.08)" }}
                                    >
                                        <i className="ti ti-home-check" style={{ fontSize: "18px" }}></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                                            Willingness Offers & Unwillingness Requests
                                        </h5>
                                        <span className="text-muted extra-small" style={{ fontSize: "11px" }}>
                                            Showing active willingness allotment offers available for unwillingness response and submitted unwillingness records
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                        <i className="ti ti-circle-check fs-6"></i> Total Offers & Requests: {allDisplayRequests.length}
                                    </span>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                {allDisplayRequests.length > 0 ? (
                                    <>
                                        {/* Location Filter Tabs */}
                                        {locationNames.length > 1 && (
                                            <div className="d-flex flex-wrap align-items-center gap-2 mb-4 pb-2 border-bottom">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm rounded-pill px-3 fw-semibold ${selectedLocationTab === "ALL" ? "btn-primary shadow-xs" : "btn-light border text-muted"}`}
                                                    onClick={() => setSelectedLocationTab("ALL")}
                                                >
                                                    <i className="ti ti-layout-grid me-1"></i>
                                                    All Locations ({allDisplayRequests.length})
                                                </button>
                                                {locationNames.map((loc) => (
                                                    <button
                                                        key={loc}
                                                        type="button"
                                                        className={`btn btn-sm rounded-pill px-3 fw-semibold ${selectedLocationTab === loc ? "btn-primary shadow-xs" : "btn-light border text-muted"}`}
                                                        onClick={() => setSelectedLocationTab(loc)}
                                                    >
                                                        <i className="ti ti-map-pin me-1 text-danger"></i>
                                                        {loc} ({locationGroupedRequests[loc]?.length || 0})
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Location-Wise Cards */}
                                        <div className="d-flex flex-column gap-4">
                                            {displayedLocationNames.map((locName: string) => {
                                                const requestsInLoc = locationGroupedRequests[locName] || [];

                                                return requestsInLoc.map((reqObj: any, index: number) => {
                                                    const cat = reqObj._category;
                                                    const isWillingnessOffer = cat === "WILLINGNESS_APPROVED";
                                                    const isUnwillingnessApproved = cat === "UNWILLINGNESS_APPROVED";

                                                    const approversList = Array.isArray(reqObj.approvedBy) && reqObj.approvedBy.length > 0
                                                        ? reqObj.approvedBy
                                                        : Array.isArray(reqObj.approvers) && reqObj.approvers.length > 0
                                                            ? reqObj.approvers
                                                            : reqObj.pendingWith ? [reqObj.pendingWith] : [];

                                                    // Deduplicate approver names
                                                    const seenApprovers = new Set();
                                                    const approverNames = approversList.map((appr: any) => {
                                                        if (typeof appr === "string") return appr;
                                                        const name = appr.name || appr.employeeName || appr.approverName || "";
                                                        const empId = (appr.empId || appr.employeeNumber || reqObj.approverId || "").toString().replace(/^0+/, '');
                                                        const label = name ? `${name} (${empId || '00019195'})` : (empId ? `ID: ${empId}` : "");
                                                        if (!label || seenApprovers.has(label)) return null;
                                                        seenApprovers.add(label);
                                                        return label;
                                                    }).filter(Boolean).join(", ") || (reqObj.approverId || reqObj.approvedByName ? `${reqObj.approvedByName || reqObj.approverId}` : "-");

                                                    const flatsList = Array.isArray(reqObj.flats) && reqObj.flats.length > 0
                                                        ? reqObj.flats
                                                        : Array.isArray(reqObj.flatList) && reqObj.flatList.length > 0
                                                            ? reqObj.flatList
                                                            : Array.isArray(reqObj.willingnessFlats) && reqObj.willingnessFlats.length > 0
                                                                ? reqObj.willingnessFlats
                                                                : Array.isArray(reqObj.willingnessDetails) && reqObj.willingnessDetails.length > 0
                                                                    ? reqObj.willingnessDetails
                                                                    : (reqObj.flatNo || reqObj.flat_no || reqObj.flat || reqObj.quarterNo || reqObj.quarter_no ? [reqObj] : []);

                                                    return (
                                                        <div
                                                            key={`${locName}_${index}`}
                                                            className="card border shadow-sm mb-0 overflow-hidden"
                                                            style={{
                                                                borderRadius: "12px",
                                                                borderColor: isWillingnessOffer ? "#bbf7d0" : (isUnwillingnessApproved ? "#fecaca" : "#cbd5e1")
                                                            }}
                                                        >
                                                            <div
                                                                className="card-header py-3 px-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
                                                                style={{
                                                                    backgroundColor: isWillingnessOffer ? "#f0fdf4" : (isUnwillingnessApproved ? "#fff5f5" : "#f8fafc"),
                                                                    borderBottom: "1px solid #e2e8f0"
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className={`badge ${isWillingnessOffer ? "bg-success" : "bg-danger"} rounded-pill px-3 py-1.5 fs-6 d-inline-flex align-items-center gap-1`}>
                                                                        <i className="ti ti-map-pin" style={{ fontSize: "14px" }}></i>
                                                                        {locName}
                                                                    </span>
                                                                    <h6 className="fw-bold text-dark mb-0 ms-1" style={{ fontSize: "15px" }}>
                                                                        {isWillingnessOffer ? "Approved Willingness Offer #" : "Unwillingness Request #"}{reqObj.approvalId || reqObj.willingnessId || reqObj.id || index + 1}
                                                                    </h6>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {isWillingnessOffer ? (
                                                                        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                                                            <i className="ti ti-circle-check fs-6"></i>
                                                                            APPROVED WILLINGNESS OFFER
                                                                        </span>
                                                                    ) : isUnwillingnessApproved ? (
                                                                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                                                            <i className="ti ti-ban fs-6"></i>
                                                                            APPROVED UNWILLINGNESS
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                                                            <i className="ti ti-clock fs-6"></i>
                                                                            UNWILLINGNESS PENDING
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="card-body p-4 bg-white">
                                                                {isWillingnessOffer && (
                                                                    <div className="alert alert-success border-0 bg-success-subtle text-success-emphasis p-3 rounded-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <i className="ti ti-info-circle fs-5 text-success"></i>
                                                                            <span className="small fw-medium">
                                                                                You have been offered quarter options under this approved willingness. You may submit an <strong>Unwillingness</strong> request if you choose to decline this offer.
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger rounded-pill px-3 fw-bold"
                                                                            onClick={() => handleOpenUnwillingnessModal(reqObj)}
                                                                        >
                                                                            <i className="ti ti-ban me-1"></i> Submit Unwillingness
                                                                        </button>
                                                                    </div>
                                                                )}

                                                                <div className="row g-3">
                                                                    <div className="col-lg-3 col-md-6">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                                            {isWillingnessOffer ? "Registration Ref" : "Unwillingness / Registration Ref"}
                                                                        </div>
                                                                        <div className="fw-bold text-primary mt-1" style={{ fontSize: "14px" }}>
                                                                            {reqObj.unwillingnessReferenceNumber || reqObj.willingnessReferenceNumber || data.referenceNumber || reqObj.registrationReferenceNumber || "-"}
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-3 col-md-6">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Quarter Type & Pool</div>
                                                                        <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>
                                                                            {reqObj.quarterType || data.quarterTypeEligibility?.type || "-"} <span className="text-secondary fw-semibold">({reqObj.poolName || data.pool?.name || "-"})</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-3 col-md-6">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                                            {isWillingnessOffer ? "Offer / Creation Date" : "Unwillingness / Request Date"}
                                                                        </div>
                                                                        <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>
                                                                            {formatDate(reqObj.unwillingnessDate || reqObj.debarmentDate || reqObj.createdAt || reqObj.offerDate, true)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-3 col-md-6">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Approval Date</div>
                                                                        <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>
                                                                            {formatDate(reqObj.approvedAt || reqObj.updatedAt, true)}
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-6 col-md-6 border-top pt-3">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Approved By</div>
                                                                        <div className="fw-bold text-dark mt-1" style={{ fontSize: "13px" }}>
                                                                            <i className="ti ti-user-check text-success me-1"></i>
                                                                            {approverNames}
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-6 col-md-6 border-top pt-3">
                                                                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                                            {isWillingnessOffer ? "Approver Observations / Remarks" : "Employee Reason / Remarks for Unwillingness"}
                                                                        </div>
                                                                        <div className="p-2 px-3 bg-light rounded border text-dark fw-medium mt-1" style={{ fontSize: "13px" }}>
                                                                            {reqObj.unwillingnessRemarks || reqObj.userRemarks || reqObj.remarks || reqObj.reason || reqObj.unwillingnessDetails?.remarks || "No remarks provided"}
                                                                        </div>
                                                                    </div>

                                                                    {/* Associated Flats Listing Location-Wise */}
                                                                    {flatsList.length > 0 && (
                                                                        <div className="col-12 border-top pt-3">
                                                                            <div className="text-dark small fw-bold text-uppercase mb-2" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                                                                                {isWillingnessOffer ? `Associated Flats Offered (${flatsList.length})` : `Associated Flats Declined (${flatsList.length})`}
                                                                            </div>
                                                                            <div className="d-flex flex-column gap-2">
                                                                                {flatsList.map((flatItem: any, flatIdx: number) => {
                                                                                    const flatNoFormatted = formatFlatNoWithBlock(flatItem, flatItem.block, data);
                                                                                    return (
                                                                                        <div key={flatIdx} className="p-3 bg-light-subtle rounded-3 border d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ borderColor: "#e2e8f0" }}>
                                                                                            <div className="d-flex align-items-center gap-3">
                                                                                                <span className={`badge ${isWillingnessOffer ? "bg-success" : "bg-danger"} text-white px-3 py-1.5 fs-14 fw-bold shadow-xs`}>
                                                                                                    <i className="ti ti-building me-1"></i>
                                                                                                    {flatNoFormatted}
                                                                                                </span>
                                                                                                {flatItem.willingnessReferenceNumber && (
                                                                                                    <div>
                                                                                                        <div className="text-muted small fw-medium" style={{ fontSize: "12px" }}>
                                                                                                            Willingness Ref: <strong className="text-dark">{flatItem.willingnessReferenceNumber}</strong>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                            <div>
                                                                                                <span className={`badge ${isWillingnessOffer ? "bg-success-subtle text-success border border-success-subtle" : "bg-danger-subtle text-danger border border-danger-subtle"} px-3 py-1 fw-semibold`} style={{ fontSize: "11px" }}>
                                                                                                    {isWillingnessOffer ? "OFFERED FLAT" : (flatItem.unwillingnessStatus || flatItem.willingnessStatus || "DECLINED")}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        <div
                                            className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light text-muted mb-3"
                                            style={{ width: "56px", height: "56px" }}
                                        >
                                            <i className="ti ti-ban fs-2 text-secondary opacity-50"></i>
                                        </div>
                                        <h6 className="fw-bold text-dark mb-1">No Active Willingness Offers or Unwillingness Requests Found</h6>
                                        <p className="small text-muted mb-0" style={{ maxWidth: "440px", margin: "0 auto" }}>
                                            Currently, there are no approved willingness allotment offers or submitted unwillingness records for this application.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Section 3: Policy Notice */}
                        <div className="card mb-4 shadow-sm border" style={{ borderRadius: "12px", borderLeft: "4px solid #f59e0b", backgroundColor: "#fffbeb" }}>
                            <div className="card-body p-3 p-md-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center bg-warning text-white flex-shrink-0 shadow-xs"
                                        style={{ width: "42px", height: "42px" }}
                                    >
                                        <i className="ti ti-info-square-rounded fs-3"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-dark fw-bold mb-2 fs-15">
                                            DMRC Staff Quarter Rules on Refusal / Unwillingness:
                                        </h6>
                                        <ul className="mb-0 ps-3 text-dark fw-medium" style={{ lineHeight: "1.8", fontSize: "13px" }}>
                                            <li>If an employee declines or fails to respond to an offer of allotment in their priority turn, they are treated as <strong>Unwilling</strong>.</li>
                                            <li>In accordance with staff quarter policy, the applicant is debarred / de-listed from the priority list for a duration of <strong>1 (one) year</strong> or until the commencement of the next general allotment cycle.</li>
                                            <li>Employees wishing to re-enter the priority list post-debarment must submit a fresh registration or renewal application per company guidelines.</li>
                                        </ul>
                                    </div>
                        </div>
                    </div>
                </div>

                {/* Submit Unwillingness Modal */}
                <Modal
                    title={
                        <div className="d-flex align-items-center gap-2 text-danger fw-bold">
                            <i className="ti ti-ban fs-4"></i>
                            <span>Submit Quarter Unwillingness / Declination</span>
                        </div>
                    }
                    open={unwillingnessModalOpen}
                    onCancel={() => {
                        if (!isSubmittingUnwillingness) {
                            setUnwillingnessModalOpen(false);
                            setSelectedOfferForUnwillingness(null);
                            setUnwillingnessRemarks("");
                        }
                    }}
                    footer={[
                        <button
                            key="cancel"
                            type="button"
                            className="btn btn-light rounded-pill px-4 me-2"
                            disabled={isSubmittingUnwillingness}
                            onClick={() => {
                                setUnwillingnessModalOpen(false);
                                setSelectedOfferForUnwillingness(null);
                                setUnwillingnessRemarks("");
                            }}
                        >
                            Cancel
                        </button>,
                        <button
                            key="submit"
                            type="button"
                            className="btn btn-danger rounded-pill px-4 fw-bold"
                            disabled={isSubmittingUnwillingness}
                            onClick={handleSubmitUnwillingness}
                        >
                            {isSubmittingUnwillingness ? (
                                <>
                                    <Spin size="small" className="me-2 text-white" /> Submitting...
                                </>
                            ) : (
                                "Submit Unwillingness"
                            )}
                        </button>
                    ]}
                    destroyOnClose
                >
                    {selectedOfferForUnwillingness && (
                        <div className="py-2">
                            <div className="p-3 bg-light rounded-3 border mb-3">
                                <div className="row g-2 text-dark small">
                                    <div className="col-6">
                                        <span className="text-muted d-block">Location:</span>
                                        <strong className="text-primary fs-14">
                                            {selectedOfferForUnwillingness.location || selectedOfferForUnwillingness.locationName || "Location Offer"}
                                        </strong>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted d-block">Registration Ref:</span>
                                        <strong>{data.referenceNumber || selectedOfferForUnwillingness.willingnessReferenceNumber || "-"}</strong>
                                    </div>
                                    <div className="col-6 mt-2">
                                        <span className="text-muted d-block">Quarter Type:</span>
                                        <strong>{selectedOfferForUnwillingness.quarterType || data.quarterTypeEligibility?.type || "-"}</strong>
                                    </div>
                                    <div className="col-6 mt-2">
                                        <span className="text-muted d-block">Pool Category:</span>
                                        <strong>{selectedOfferForUnwillingness.poolName || data.pool?.name || "-"}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="alert alert-warning border-warning-subtle text-warning-emphasis small mb-3">
                                <i className="ti ti-alert-triangle me-1"></i>
                                <strong>Important Notice:</strong> Declining this quarter offer will treat you as <strong>Unwilling</strong>. Per DMRC staff quarter rules, cooldown/debarment restrictions will apply on future allotment cycles.
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark small">
                                    Reason / Remarks for Declination <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    placeholder="Enter reason or remarks for declining this quarter offer..."
                                    value={unwillingnessRemarks}
                                    onChange={(e) => setUnwillingnessRemarks(e.target.value)}
                                    disabled={isSubmittingUnwillingness}
                                ></textarea>
                            </div>
                        </div>
                    )}
                </Modal>
            </>
        )}
    </div>
</div>
    );
};

export default Unwillingness;
